// src/utils/modelesManuels.ts — SCE Issues
// Modèles choisis à la main à l'import Windchill (Lot 18). Pur, testable.
//
// ─── Pourquoi ───
//
// Le résolveur (`resoudreModele`) laisse vide ce qu'il ne sait pas trancher.
// Jusqu'ici, il fallait compléter la colonne Modèle à la main dans
// SharePoint, équipement par équipement, et recommencer si l'import suivant
// ne résolvait toujours pas. Désormais l'aperçu de l'import regroupe les cas
// par « signature » et l'administrateur choisit le modèle UNE fois par
// groupe ; le choix est mémorisé pour les imports suivants.
//
// ─── La signature ───
//
// Ce qui fait que deux équipements ont forcément le même modèle :
//   - le NVS de la fourniture (« NVS40964 ») — une référence produit ;
//   - à défaut, la fourniture et la désignation, normalisées.
// Une ligne sans fourniture ni désignation n'a pas de signature fiable :
// elle est groupée par projet et catégorie, pour le confort, mais son choix
// n'est JAMAIS mémorisé (il s'appliquerait à n'importe quelle ligne vide).

import {
  CATALOGUE_EQUIPEMENTS,
  modeleParLibelle,
  nvsDeFourniture,
  type ModeleEquipement,
} from '@/config/equipements.catalogue'
import type { CategorieEquipement } from '@/types/issue.types'

/** Nom du bloc dans le JSON de la ligne `__global__` de SCE_UserPrefs. */
export const BLOC_MODELES_MANUELS = 'modelesManuels'

/** Signature → libellé exact du catalogue. */
export type Correspondances = Readonly<Record<string, string>>

export interface Signature {
  cle: string
  /** Faux pour une ligne sans fourniture ni désignation. */
  memorisable: boolean
}

function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export function signature(ligne: {
  fourniture: string
  designation: string
  categorie: CategorieEquipement | null
  pn?: string
}): Signature {
  const nvs = nvsDeFourniture(ligne.fourniture, ligne.categorie)
  if (nvs) return { cle: nvs, memorisable: true }
  const f = normaliser(ligne.fourniture)
  const d = normaliser(ligne.designation)
  if (f || d) return { cle: `txt:${f}|${d}`, memorisable: true }
  return { cle: `vide:${normaliser(ligne.pn ?? '')}:${ligne.categorie ?? ''}`, memorisable: false }
}

/** Modèles proposables pour une catégorie (un -SC ne reçoit qu'une colonne). */
export function modelesPour(categorie: CategorieEquipement | null): ModeleEquipement[] {
  return CATALOGUE_EQUIPEMENTS.filter((m) => categorie === null || m.categorie === categorie)
}

/** Le libellé existe dans le catalogue et convient à la catégorie. */
export function modeleAdmis(libelle: string, categorie: CategorieEquipement | null): boolean {
  const m = modeleParLibelle(libelle)
  return m !== null && (categorie === null || m.categorie === categorie)
}

/**
 * Lecture tolérante du bloc mémorisé : un libellé sorti du catalogue depuis
 * (modèle renommé, retiré) est écarté plutôt que de faire rejeter l'écriture.
 */
export function lireCorrespondances(brut: unknown): Record<string, string> {
  if (!brut || typeof brut !== 'object' || Array.isArray(brut)) return {}
  const table = (brut as { correspondances?: unknown }).correspondances
  if (!table || typeof table !== 'object' || Array.isArray(table)) return {}
  const lu: Record<string, string> = {}
  for (const [cle, valeur] of Object.entries(table as Record<string, unknown>)) {
    if (typeof valeur === 'string' && modeleParLibelle(valeur)) lu[cle] = valeur
  }
  return lu
}

/**
 * Nouveau contenu du bloc : les choix de cet import par-dessus les anciens.
 * `null` = « laisser vide » : oublie une correspondance mémorisée.
 */
export function fusionnerCorrespondances(
  anciennes: Correspondances,
  choix: ReadonlyMap<string, string | null>,
): { version: 1; correspondances: Record<string, string> } {
  const correspondances: Record<string, string> = { ...anciennes }
  for (const [cle, libelle] of choix) {
    if (cle.startsWith('vide:')) continue
    if (libelle === null) delete correspondances[cle]
    else correspondances[cle] = libelle
  }
  return { version: 1, correspondances }
}
